import { useState, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { Search, X } from 'lucide-react';
import api from '../../services/api';
import { useLanguage } from '../../context/LanguageContext';
import SEOHead from '../../components/common/SEOHead';
import { usePageSeo } from '../../hooks/useSeoConfig';
import BlogCard from './components/BlogCard';

const BlogList = () => {
    const location = useLocation();
    const { language } = useLanguage();
    const inDashboard = location.pathname.startsWith('/dashboard');
    const [posts, setPosts] = useState([]);
    const [categories, setCategories] = useState([]);
    const [selectedCategory, setSelectedCategory] = useState(null);
    const [page, setPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [total, setTotal] = useState(0);
    const [loading, setLoading] = useState(true);
    const [searchInput, setSearchInput] = useState('');
    const [searchQuery, setSearchQuery] = useState('');
    const debounceRef = useRef(null);

    const LIMIT = 9;

    // Debounce search input → searchQuery
    useEffect(() => {
        clearTimeout(debounceRef.current);
        debounceRef.current = setTimeout(() => setSearchQuery(searchInput.trim()), 350);
        return () => clearTimeout(debounceRef.current);
    }, [searchInput]);

    useEffect(() => {
        setPage(1);
    }, [language, selectedCategory, searchQuery]);

    useEffect(() => {
        setLoading(true);
        const params = { action: 'list', lang: language, page, limit: LIMIT };
        if (selectedCategory) params.category = selectedCategory;
        if (searchQuery) params.search = searchQuery;

        api.get('/blog.php', { params })
            .then(res => {
                setPosts(res.data.posts || []);
                setTotalPages(res.data.pages || 1);
                setTotal(res.data.total || 0);
            })
            .catch(() => setPosts([]))
            .finally(() => setLoading(false));
    }, [language, selectedCategory, searchQuery, page]);

    useEffect(() => {
        api.get('/blog.php', { params: { action: 'categories' } })
            .then(res => setCategories(res.data || []))
            .catch(() => {});
    }, []);

    const categoryList = categories.map(c => language === 'en' ? c.category_en : c.category_fr).filter(Boolean);

    const searchBar = (
        <div className="relative mb-6">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 dark:text-gray-500 pointer-events-none" />
            <input
                type="text"
                value={searchInput}
                onChange={e => setSearchInput(e.target.value)}
                placeholder={language === 'fr' ? 'Rechercher un article...' : 'Search articles...'}
                className="w-full pl-11 pr-10 py-3 text-sm bg-white dark:bg-white/5 border border-gray-200 dark:border-white/[0.08] rounded-xl text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500 transition"
            />
            {searchInput && (
                <button
                    onClick={() => setSearchInput('')}
                    className="absolute right-3 top-1/2 -translate-y-1/2 p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition"
                >
                    <X className="w-4 h-4" />
                </button>
            )}
        </div>
    );

    const CategoryFilters = () => categoryList.length > 0 && (
        <div className="flex flex-wrap gap-2 mb-8">
            <button
                onClick={() => setSelectedCategory(null)}
                className={`px-4 py-1.5 rounded-full text-sm font-medium transition ${
                    !selectedCategory
                        ? 'bg-blue-600 text-white'
                        : 'bg-white dark:bg-white/10 text-gray-700 dark:text-white/60 hover:bg-gray-100 dark:hover:bg-white/20 border border-gray-200 dark:border-transparent'
                }`}
            >
                {language === 'en' ? 'All' : 'Tous'}
            </button>
            {categoryList.map(cat => (
                <button
                    key={cat}
                    onClick={() => setSelectedCategory(cat)}
                    className={`px-4 py-1.5 rounded-full text-sm font-medium transition ${
                        selectedCategory === cat
                            ? 'bg-blue-600 text-white'
                            : 'bg-white dark:bg-white/10 text-gray-700 dark:text-white/60 hover:bg-gray-100 dark:hover:bg-white/20 border border-gray-200 dark:border-transparent'
                    }`}
                >
                    {cat}
                </button>
            ))}
        </div>
    );

    const Grid = () => (
        <>
            {loading && (
                <div className="flex justify-center py-20">
                    <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 border-blue-500" />
                </div>
            )}
            {!loading && posts.length === 0 && (
                <div className="text-center py-20 text-gray-400">
                    <p className="text-lg">{language === 'en' ? 'No articles found.' : 'Aucun article trouvé.'}</p>
                </div>
            )}
            {!loading && posts.length > 0 && (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                    {posts.map(post => (
                        <BlogCard key={post.idBlog} post={post} />
                    ))}
                </div>
            )}
            {totalPages > 1 && (
                <div className="flex items-center justify-center gap-4 mt-12">
                    <button
                        onClick={() => setPage(p => Math.max(1, p - 1))}
                        disabled={page <= 1}
                        className="px-5 py-2 rounded-xl bg-white dark:bg-white/[0.08] border border-gray-200 dark:border-white/15 text-sm font-medium text-gray-900 dark:text-white disabled:opacity-30 hover:bg-gray-50 dark:hover:bg-white/15 transition"
                    >
                        {language === 'en' ? 'Previous' : 'Précédent'}
                    </button>
                    <span className="text-sm font-medium text-gray-700 dark:text-white/80">
                        {page} / {totalPages} — {total} {language === 'en' ? 'articles' : 'articles'}
                    </span>
                    <button
                        onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                        disabled={page >= totalPages}
                        className="px-5 py-2 rounded-xl bg-white dark:bg-white/[0.08] border border-gray-200 dark:border-white/15 text-sm font-medium text-gray-900 dark:text-white disabled:opacity-30 hover:bg-gray-50 dark:hover:bg-white/15 transition"
                    >
                        {language === 'en' ? 'Next' : 'Suivant'}
                    </button>
                </div>
            )}
        </>
    );

    const seo = usePageSeo('page_blog');

    // Layout (container/padding/bg) comes from parent: PublicContentLayout or AccountLayout.
    return (
        <>
            {!inDashboard && (
                <SEOHead
                    title={language === 'fr' ? (seo.title_fr || 'Blogue - Articles sur l\'aviation') : (seo.title_en || 'Blog - Aviation Articles')}
                    description={language === 'fr' ? seo.desc_fr : seo.desc_en}
                    keywords={language === 'fr' ? seo.keywords_fr : seo.keywords_en}
                    canonical="/blog"
                />
            )}
            {/* Banner card */}
            <div className="relative overflow-hidden rounded-[2rem] p-10 h-64 bg-gradient-to-br from-[#0077b6] via-[#023e8a] to-[#03045e] border border-white/5 mb-8 shadow-xl text-white">
                <div className="absolute inset-0 opacity-20 pointer-events-none"
                    style={{ backgroundImage: 'linear-gradient(90deg, transparent 50%, rgba(0,0,0,0.5) 50%)', backgroundSize: '4px 100%' }}
                />
                <div className="relative z-10 h-full flex flex-col justify-between">
                    <div className="flex items-center gap-4">
                        <div className="p-3 bg-white/20 rounded-2xl">
                            <svg className="w-6 h-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 20H5a2 2 0 01-2-2V6a2 2 0 012-2h10a2 2 0 012 2v1m2 13a2 2 0 01-2-2V7m2 13a2 2 0 002-2V9a2 2 0 00-2-2h-2m-4-3H9M7 16h6M7 12h6m-6-4h4" />
                            </svg>
                        </div>
                        <h1 className="text-4xl font-black text-white tracking-tight">{language === 'en' ? 'Blog' : 'Blogue'}</h1>
                    </div>
                    <p className="text-white/60 text-lg max-w-sm leading-snug">
                        {language === 'en'
                            ? 'Articles and news about becoming a pilot in Canada.'
                            : 'Articles et actualités pour devenir pilote au Canada.'}
                    </p>
                </div>
                <div className="absolute right-[-40px] bottom-[-40px] opacity-20 pointer-events-none">
                    <svg className="w-72 h-72 text-white stroke-[0.5]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 20H5a2 2 0 01-2-2V6a2 2 0 012-2h10a2 2 0 012 2v1m2 13a2 2 0 01-2-2V7m2 13a2 2 0 002-2V9a2 2 0 00-2-2h-2m-4-3H9M7 16h6M7 12h6m-6-4h4" />
                    </svg>
                </div>
            </div>
            {searchBar}
            <CategoryFilters />
            <Grid />
        </>
    );
};

export default BlogList;
